import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, throwError, Observable, Subject, take } from 'rxjs';

// Shared subject for token refresh to prevent concurrent refresh requests
let refreshTokenInProgress = false;
let refreshTokenSubject: Subject<boolean> | null = null;

/**
 * HTTP Interceptor that automatically adds the Authorization header to all requests
 * except for the login and refresh token endpoints. Also handles token refresh on 401 errors.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Skip adding token for login and refresh token requests
  if (req.url.includes('/Auth/Login') || req.url.includes('/Auth/RefreshToken')) {
    return next(req);
  }

  // Get the auth service and token
  const token = authService.getAccessToken();

  // If there's no token, proceed with the original request
  if (!token) {
    return next(req);
  }

  // Clone the request and add the Authorization header
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  // Send the request and handle 401 errors
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get a 401 error, try to refresh the token
      if (error.status === 401) {
        // If a refresh is already in progress, wait for it
        if (refreshTokenInProgress) {
          return new Observable<HttpEvent<unknown>>(observer => {
            refreshTokenSubject?.pipe(take(1)).subscribe({
              next: () => {
                // Retry the original request with the new token
                const newToken = authService.getAccessToken();
                if (!newToken) {
                  observer.error(error);
                  return;
                }

                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`
                  }
                });

                next(retryReq).subscribe({
                  next: (response) => observer.next(response),
                  error: (err) => observer.error(err),
                  complete: () => observer.complete()
                });
              },
              error: (err) => observer.error(err)
            });
          });
        }

        // Mark refresh as in progress
        refreshTokenInProgress = true;
        refreshTokenSubject = new Subject<boolean>();

        return authService.refreshToken().pipe(
          switchMap(() => {
            // Notify waiting requests that refresh is complete
            refreshTokenInProgress = false;
            refreshTokenSubject?.next(true);
            refreshTokenSubject?.complete();
            refreshTokenSubject = null;

            // Retry the original request with the new token
            const newToken = authService.getAccessToken();
            if (!newToken) {
              return throwError(() => error);
            }

            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`
              }
            });

            return next(retryReq);
          }),
          catchError((refreshError) => {
            // If refresh fails, clear the session and return the error
            refreshTokenInProgress = false;
            refreshTokenSubject?.error(refreshError);
            refreshTokenSubject = null;
            authService.logout();
            return throwError(() => refreshError);
          })
        );
      }

      // For other errors, just pass them through
      return throwError(() => error);
    })
  );
};
